import React from "react";
import { Box, Text, Group, Progress, useMantineTheme } from "@mantine/core";
import { motion } from "framer-motion";

export interface ProgressIndicatorProps {
  /** Whether to show progress indicator */
  show: boolean;

  /** Current progress percentage (0-100) */
  timeProgress: number;

  /** Current content block index */
  currentIndex: number;

  /** Total number of content blocks */
  totalBlocks: number;

  /** Progress indicator position */
  position?: "bottom" | "top";

  /** Progress indicator style */
  style?: "bar" | "dots" | "minimal";
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  show,
  timeProgress,
  currentIndex,
  totalBlocks,
  position = "bottom",
  style = "bar",
}) => {
  const theme = useMantineTheme();

  if (!show || totalBlocks <= 1) return null;

  if (style === "dots") {
    return (
      <Box
        style={{
          position: "absolute",
          [position]: theme.spacing.md,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
        }}
      >
        <Group gap={theme.spacing.xs} justify="center">
          {Array.from({ length: totalBlocks }).map((_, index) => (
            <motion.div
              key={index}
              initial={{ scale: 0.8, opacity: 0.6 }}
              animate={{
                scale: index === currentIndex ? 1.2 : 0.8,
                opacity: index === currentIndex ? 1 : 0.6,
              }}
              transition={{ duration: 0.3 }}
              style={{
                width: index === currentIndex ? "12px" : "8px",
                height: index === currentIndex ? "12px" : "8px",
                borderRadius: "50%",
                backgroundColor:
                  index === currentIndex
                    ? "white"
                    : index < currentIndex
                    ? "rgba(255, 255, 255, 0.8)"
                    : "rgba(255, 255, 255, 0.4)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {index === currentIndex && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: timeProgress / 100 }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "50%",
                    backgroundColor: theme.colors.primary[5],
                    transformOrigin: "center",
                  }}
                />
              )}
            </motion.div>
          ))}
        </Group>
      </Box>
    );
  }

  if (style === "minimal") {
    return (
      <Box
        style={{
          position: "absolute",
          [position]: theme.spacing.sm,
          right: theme.spacing.md,
          zIndex: 10,
        }}
      >
        <Text
          size="xs"
          style={{
            color: "white",
            fontSize: theme.fontSizes.xs,
            fontWeight: 500,
            textShadow: "0 1px 3px rgba(0, 0, 0, 0.5)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            borderRadius: theme.radius.sm,
            backdropFilter: "blur(8px)",
          }}
        >
          {currentIndex + 1} / {totalBlocks}
        </Text>
      </Box>
    );
  }

  // Default: bar style
  return (
    <Box
      style={{
        position: "relative",
        height: "30px",
        [position]: 0,
        left: 0,
        right: 0,
        zIndex: 1, // Lower z-index so it appears under content
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        backdropFilter: "blur(4px)",
      }}
    >
      <Box style={{ padding: 7 }}>
        <Group justify="space-between" align="center" mb={theme.spacing.xs}>
          {/* Progress bar container */}
          <Box
            style={{
              flex: 1,
              marginRight: theme.spacing.md,
              position: "relative",
            }}
          >
            <Progress
              value={timeProgress}
              size="xs"
              color="white"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              }}
            />

            {/* Block indicators on progress bar */}
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              {Array.from({ length: totalBlocks }).map((_, index) => (
                <Box
                  key={index}
                  style={{
                    position: "absolute",
                    left: `${(index / (totalBlocks - 1)) * 100}%`,
                    transform: "translateX(-50%)",
                    width: "2px",
                    height: "100%",
                    backgroundColor:
                      index <= currentIndex
                        ? "rgba(255, 255, 255, 0.8)"
                        : "rgba(255, 255, 255, 0.3)",
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Block counter */}
          <Text
            style={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "0.7rem",
              fontWeight: 500,
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.7)",
              whiteSpace: "nowrap",
            }}
          >
            {currentIndex + 1} / {totalBlocks}
          </Text>
        </Group>
      </Box>
    </Box>
  );
};
